import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import {
  formatReportingLifecycleLabel,
  formatNullableCount,
  formatNullableDate,
  formatReviewScore,
} from '../reportingUtils';
import type {
  ReportingApp,
  ReportingCategorySummary,
  ReportingSummary,
} from '../types';
import {
  LifetimeTag,
  RatingDistributionBar,
  RatingStars,
  RatioBar,
  ReportingPanel,
  ReportingSectionHeader,
  ReportingStatusPill,
} from './ReportingChrome';
import { ReportingAppIcon } from './ReportingAppIcon';
import { ReportingMetricCard } from './ReportingMetricCard';

interface PublicationSummarySectionProps {
  summary: ReportingSummary;
  summaryByCategory: ReportingCategorySummary[];
  apps: ReportingApp[];
  isStoreMetricsLoading?: boolean;
  isStoreMetricsUnavailable?: boolean;
  onAppFocus?: (app: ReportingApp) => void;
}

type MetricCardTone = 'default' | 'teal' | 'amber';
type MetricCardEmphasis = 'subtle' | 'highlight';

interface MetricCardViewModel {
  label: string;
  value: string;
  hint?: string;
  tone?: MetricCardTone;
  emphasis?: MetricCardEmphasis;
  trailing?: ReactNode;
  footer?: ReactNode;
  isLoading?: boolean;
  loadingLabel?: string;
}

interface RatingBuckets {
  one: number | null;
  two: number | null;
  three: number | null;
  four: number | null;
  five: number | null;
}

export function PublicationSummarySection({
  summary,
  summaryByCategory,
  apps,
  isStoreMetricsLoading = false,
  isStoreMetricsUnavailable = false,
  onAppFocus,
}: PublicationSummarySectionProps) {
  const ratingBuckets = aggregateRatingBuckets(apps);
  const totalReviews = summary.totalReviews ?? 0;
  const positiveReviews = (ratingBuckets.four ?? 0) + (ratingBuckets.five ?? 0);
  const sentimentPct =
    totalReviews > 0 ? Math.round((positiveReviews / totalReviews) * 100) : 0;
  const metricCards = [
    getAppsPublishedMetric(summary),
    getLiveListingsMetric(
      summary,
      isStoreMetricsLoading,
      isStoreMetricsUnavailable,
    ),
    getReviewsMetric(
      summary,
      ratingBuckets,
      isStoreMetricsLoading,
      isStoreMetricsUnavailable,
    ),
    getAverageRatingMetric(
      summary,
      totalReviews,
      sentimentPct,
      isStoreMetricsLoading,
      isStoreMetricsUnavailable,
    ),
  ];

  const topByUpdates = sortAndCap(
    apps,
    (a, b) => b.updatesInPeriod - a.updatesInPeriod,
    5,
    (app) => app.updatesInPeriod > 0,
  );
  const topRated = sortAndCap(
    apps,
    (a, b) =>
      reviewWeight(b.averageReviewScore, b.reviews) -
      reviewWeight(a.averageReviewScore, a.reviews),
    5,
    (app) => (app.reviews ?? 0) >= 5,
  );
  const recentlyPublished = sortAndCap(
    apps,
    (a, b) =>
      (b.latestPublishedAtInRange ?? b.latestPublishedAt ?? 0) -
      (a.latestPublishedAtInRange ?? a.latestPublishedAt ?? 0),
    5,
    (app) => (app.latestPublishedAtInRange ?? app.latestPublishedAt) !== null,
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {metricCards.map((metric) => (
          <ReportingMetricCard key={metric.label} {...metric} />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        <ReportingPanel>
          <div className="p-5">
            <ReportingSectionHeader
              title="Rating distribution"
              subtitle="Lifetime store ratings for apps active in this range"
              trailing={
                <span className="tabular-nums text-neutral-400">
                  {getRatingDistributionTrailingLabel(
                    totalReviews,
                    isStoreMetricsLoading,
                    isStoreMetricsUnavailable,
                  )}
                </span>
              }
            />

            <div className="mt-5">
              {renderRatingDistributionContent(
                ratingBuckets,
                totalReviews,
                isStoreMetricsLoading,
                isStoreMetricsUnavailable,
              )}
            </div>

            {shouldRenderSentimentTiles(
              totalReviews,
              isStoreMetricsLoading,
              isStoreMetricsUnavailable,
            ) && (
              <div className="mt-5 grid grid-cols-3 gap-2.5 border-t border-white/[0.04] pt-4">
                <SentimentTile
                  label="Positive"
                  hint="4★ + 5★"
                  value={positiveReviews}
                  total={totalReviews}
                  tone="teal"
                />
                <SentimentTile
                  label="Neutral"
                  hint="3★"
                  value={ratingBuckets.three ?? 0}
                  total={totalReviews}
                  tone="default"
                />
                <SentimentTile
                  label="Critical"
                  hint="1★ + 2★"
                  value={(ratingBuckets.one ?? 0) + (ratingBuckets.two ?? 0)}
                  total={totalReviews}
                  tone="rose"
                />
              </div>
            )}
          </div>
        </ReportingPanel>

        <ReportingPanel>
          <div className="p-5">
            <ReportingSectionHeader
              title="Top rated"
              subtitle="Highest lifetime average, weighted by review count"
            />
            <div className="mt-4">
              {renderTopRatedContent(
                topRated,
                isStoreMetricsLoading,
                isStoreMetricsUnavailable,
                onAppFocus,
              )}
            </div>
          </div>
        </ReportingPanel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ReportingPanel>
          <div className="p-5">
            <ReportingSectionHeader
              title="Most updated in range"
              subtitle="Apps with the most publish events"
            />
            <div className="mt-4">
              <Leaderboard
                apps={topByUpdates}
                emptyMessage="No publish activity recorded in this range."
                onAppFocus={onAppFocus}
                renderTrailing={(app) => (
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm font-semibold tabular-nums text-white">
                      {app.updatesInPeriod.toLocaleString()}
                    </span>
                    <span className="text-[11px] text-neutral-500">
                      {app.lifecycle === 'new' ? 'launch' : 'update'}
                    </span>
                  </div>
                )}
              />
            </div>
          </div>
        </ReportingPanel>

        <ReportingPanel>
          <div className="p-5">
            <ReportingSectionHeader
              title="Recently published"
              subtitle="Latest publish-like signals in range"
            />
            <div className="mt-4">
              <Leaderboard
                apps={recentlyPublished}
                emptyMessage="No publish dates in range yet."
                onAppFocus={onAppFocus}
                renderTrailing={(app) => (
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs tabular-nums text-neutral-300">
                      {formatNullableDate(
                        app.latestPublishedAtInRange ??
                          app.latestPublishedAt ??
                          null,
                        '—',
                      )}
                    </span>
                    {app.lifecycle === 'new' && (
                      <ReportingStatusPill
                        label={formatReportingLifecycleLabel(app.lifecycle)}
                        tone="teal"
                      />
                    )}
                  </div>
                )}
              />
            </div>
          </div>
        </ReportingPanel>
      </div>

      <ReportingPanel>
        <div className="p-5">
          <ReportingSectionHeader
            title="Category breakdown"
            subtitle="Publish activity in range and lifetime review totals by app category"
          />

          <div className="mt-4">
            {isStoreMetricsLoading ? (
              <SummarySkeleton label="Loading category store metrics" />
            ) : (
              <CategoryGrid
                rows={summaryByCategory}
                storeMetricsUnavailable={isStoreMetricsUnavailable}
              />
            )}
          </div>
        </div>
      </ReportingPanel>
    </div>
  );
}

function getAppsPublishedMetric(
  summary: ReportingSummary,
): MetricCardViewModel {
  let newAppsShare = 0;
  if (summary.totalAppsPublished > 0) {
    newAppsShare = Math.round(
      (summary.newAppsPublished / summary.totalAppsPublished) * 100,
    );
  }

  return {
    label: 'Apps Published',
    value: summary.totalAppsPublished.toLocaleString(),
    tone: 'teal',
    emphasis: 'highlight',
    hint: 'Distinct apps with publish-like signals in range',
    footer: (
      <RatioBar
        numerator={summary.newAppsPublished}
        denominator={summary.totalAppsPublished}
        numeratorLabel={`${newAppsShare}% new`}
        denominatorLabel={`${summary.existingAppsPublished.toLocaleString()} returning`}
      />
    ),
  };
}

function getLiveListingsMetric(
  summary: ReportingSummary,
  isStoreMetricsLoading: boolean,
  isStoreMetricsUnavailable: boolean,
): MetricCardViewModel {
  let value = summary.appsWithStoreListing.toLocaleString();
  if (isStoreMetricsUnavailable) {
    value = 'Unavailable';
  }

  let hint = 'No published apps in range';
  if (isStoreMetricsUnavailable) {
    hint = 'Store coverage data could not be loaded';
  } else if (summary.totalAppsPublished > 0) {
    const liveListingPct = Math.round(
      (summary.appsWithStoreListing / summary.totalAppsPublished) * 100,
    );
    hint = `${liveListingPct}% of published apps have a live store listing`;
  }

  let footer: ReactNode = undefined;
  if (!isStoreMetricsUnavailable) {
    footer = (
      <RatioBar
        numerator={summary.appsWithStoreListing}
        denominator={Math.max(summary.totalAppsPublished, 1)}
        numeratorLabel="Listed"
        denominatorLabel="Published"
      />
    );
  }

  return {
    label: 'Live Listings',
    value,
    isLoading: isStoreMetricsLoading,
    loadingLabel: 'Loading store listing coverage',
    hint,
    footer,
  };
}

function getReviewsMetric(
  summary: ReportingSummary,
  ratingBuckets: RatingBuckets,
  isStoreMetricsLoading: boolean,
  isStoreMetricsUnavailable: boolean,
): MetricCardViewModel {
  const avgScoreLabel = getAverageReviewScoreLabel(summary);

  let value: string;
  if (isStoreMetricsUnavailable) {
    value = 'Unavailable';
  } else if (summary.totalReviews === null) {
    value = 'Source needed';
  } else {
    value = summary.totalReviews.toLocaleString();
  }

  let tone: MetricCardTone = 'teal';
  if (isStoreMetricsUnavailable || summary.totalReviews === null) {
    tone = 'amber';
  }

  let hint = avgScoreLabel;
  if (isStoreMetricsUnavailable) {
    hint = 'Store review data could not be loaded';
  }

  let footer: ReactNode = undefined;
  if (
    !isStoreMetricsUnavailable &&
    summary.totalReviews &&
    summary.totalReviews > 0
  ) {
    footer = (
      <RatingDistributionBar buckets={ratingBuckets} variant="compact" />
    );
  }

  return {
    label: 'Reviews',
    value,
    tone,
    emphasis: 'highlight',
    trailing: <LifetimeTag />,
    isLoading: isStoreMetricsLoading,
    loadingLabel: 'Loading store review totals',
    hint,
    footer,
  };
}

function getAverageRatingMetric(
  summary: ReportingSummary,
  totalReviews: number,
  sentimentPct: number,
  isStoreMetricsLoading: boolean,
  isStoreMetricsUnavailable: boolean,
): MetricCardViewModel {
  const avgScore = summary.averageReviewScore;

  let value = '—';
  if (isStoreMetricsUnavailable) {
    value = 'Unavailable';
  } else if (avgScore !== null && (summary.totalReviews ?? 0) !== 0) {
    value = avgScore.toFixed(2);
  }

  let tone: MetricCardTone = 'default';
  if (isStoreMetricsUnavailable) {
    tone = 'amber';
  } else if (avgScore !== null && avgScore >= 4) {
    tone = 'teal';
  }

  let hint = 'Not enough rated apps yet';
  if (isStoreMetricsUnavailable) {
    hint = 'Store rating data could not be loaded';
  } else if (sentimentPct > 0) {
    hint = `${sentimentPct}% of lifetime reviews are 4★ or higher`;
  }

  let footer: ReactNode = undefined;
  if (!isStoreMetricsUnavailable) {
    footer = (
      <div className="flex items-center justify-between gap-3">
        <RatingStars value={avgScore} size="md" />
        <span className="text-[11px] tabular-nums text-neutral-500">
          {totalReviews.toLocaleString()} lifetime reviews
        </span>
      </div>
    );
  }

  return {
    label: 'Avg Rating',
    value,
    tone,
    trailing: <LifetimeTag />,
    isLoading: isStoreMetricsLoading,
    loadingLabel: 'Loading average store rating',
    hint,
    footer,
  };
}

function getAverageReviewScoreLabel(summary: ReportingSummary): string {
  if (summary.totalReviews === null || summary.totalReviews === 0) {
    return 'No ratings yet';
  }

  const avgScore = summary.averageReviewScore;
  return `${(avgScore ?? 0).toFixed(2)} avg across ${summary.ratedApps.toLocaleString()} rated apps`;
}

function getRatingDistributionTrailingLabel(
  totalReviews: number,
  isStoreMetricsLoading: boolean,
  isStoreMetricsUnavailable: boolean,
): string {
  if (isStoreMetricsLoading) {
    return 'Loading...';
  }

  if (isStoreMetricsUnavailable) {
    return 'Unavailable';
  }

  return `${totalReviews.toLocaleString()} lifetime reviews`;
}

function renderRatingDistributionContent(
  ratingBuckets: RatingBuckets,
  totalReviews: number,
  isStoreMetricsLoading: boolean,
  isStoreMetricsUnavailable: boolean,
): ReactNode {
  if (isStoreMetricsLoading) {
    return <SummarySkeleton label="Loading rating distribution" />;
  }

  if (isStoreMetricsUnavailable) {
    return (
      <p className="text-sm text-neutral-500">
        Store review data could not be loaded.
      </p>
    );
  }

  if (totalReviews > 0) {
    return <RatingDistributionBar buckets={ratingBuckets} variant="detailed" />;
  }

  return (
    <p className="text-sm text-neutral-500">
      No lifetime ratings to aggregate for these apps yet.
    </p>
  );
}

function shouldRenderSentimentTiles(
  totalReviews: number,
  isStoreMetricsLoading: boolean,
  isStoreMetricsUnavailable: boolean,
): boolean {
  return (
    !isStoreMetricsLoading && !isStoreMetricsUnavailable && totalReviews > 0
  );
}

function renderTopRatedContent(
  topRated: ReportingApp[],
  isStoreMetricsLoading: boolean,
  isStoreMetricsUnavailable: boolean,
  onAppFocus?: (app: ReportingApp) => void,
): ReactNode {
  if (isStoreMetricsLoading) {
    return <SummarySkeleton label="Loading top rated apps" />;
  }

  if (isStoreMetricsUnavailable) {
    return (
      <p className="text-sm text-neutral-500">
        Store rating data could not be loaded.
      </p>
    );
  }

  return (
    <Leaderboard
      apps={topRated}
      emptyMessage="No rated apps with at least 5 lifetime reviews."
      onAppFocus={onAppFocus}
      renderTrailing={renderTopRatedTrailing}
    />
  );
}

function renderTopRatedTrailing(app: ReportingApp): ReactNode {
  return (
    <div className="flex flex-col items-end gap-1">
      <RatingStars value={app.averageReviewScore} showValue size="sm" />
      <span className="text-[11px] tabular-nums text-neutral-500">
        {(app.reviews ?? 0).toLocaleString()} lifetime
      </span>
    </div>
  );
}

function SummarySkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="space-y-2">
      <div className="h-3 w-3/4 animate-pulse rounded bg-neutral-800/80" />
      <div className="h-3 w-full animate-pulse rounded bg-neutral-800/70" />
      <div className="h-3 w-2/3 animate-pulse rounded bg-neutral-800/60" />
    </div>
  );
}

function SentimentTile({
  label,
  hint,
  value,
  total,
  tone,
}: {
  label: string;
  hint: string;
  value: number;
  total: number;
  tone: 'teal' | 'rose' | 'default';
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const toneClasses: Record<typeof tone, string> = {
    teal: 'text-teal-300',
    rose: 'text-rose-300',
    default: 'text-neutral-300',
  };

  return (
    <div className="rounded-lg border border-neutral-800/70 bg-neutral-950/40 p-3">
      <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-neutral-500">
        <span>{label}</span>
        <span className={cn('tabular-nums', toneClasses[tone])}>{pct}%</span>
      </div>
      <div className="mt-1.5 text-lg font-semibold tabular-nums text-white">
        {value.toLocaleString()}
      </div>
      <div className="mt-1 text-[11px] text-neutral-500">{hint}</div>
    </div>
  );
}

interface LeaderboardProps {
  apps: ReportingApp[];
  emptyMessage: string;
  onAppFocus?: (app: ReportingApp) => void;
  renderTrailing: (app: ReportingApp) => ReactNode;
}

function Leaderboard({
  apps,
  emptyMessage,
  onAppFocus,
  renderTrailing,
}: LeaderboardProps) {
  if (apps.length === 0) {
    return <p className="text-sm text-neutral-500">{emptyMessage}</p>;
  }

  return (
    <ol className="space-y-0.5">
      {apps.map((app, index) => {
        return (
          <li key={app.dappId}>
            <button
              type="button"
              onClick={onAppFocus ? () => onAppFocus(app) : undefined}
              disabled={!onAppFocus}
              className="group flex w-full items-center gap-3 rounded-lg p-1.5 text-left transition-colors hover:bg-white/[0.02] disabled:cursor-default disabled:hover:bg-transparent"
            >
              <span className="w-4 shrink-0 text-center text-[11px] tabular-nums text-neutral-600">
                {index + 1}
              </span>
              <ReportingAppIcon app={app} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-neutral-100">
                  {app.dappName}
                </div>
                <div className="truncate text-xs text-neutral-500">
                  {app.storePrimaryCategory ?? app.category}
                  {app.packageName ? ` · ${app.packageName}` : ''}
                </div>
              </div>
              <div className="shrink-0">{renderTrailing(app)}</div>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function CategoryGrid({
  rows,
  storeMetricsUnavailable = false,
}: {
  rows: ReportingCategorySummary[];
  storeMetricsUnavailable?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-neutral-500">
        No publication activity in the selected range.
      </p>
    );
  }

  const peakPublished = Math.max(...rows.map((row) => row.publishedApps), 1);

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {rows.map((row) => {
        const newShare =
          row.publishedApps > 0
            ? Math.round((row.newApps / row.publishedApps) * 100)
            : 0;
        const widthPct = Math.max(
          6,
          Math.round((row.publishedApps / peakPublished) * 100),
        );
        return (
          <div
            key={row.category}
            className="rounded-lg border border-neutral-800/70 bg-neutral-950/40 p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-neutral-100">
                  {row.category}
                </div>
                <div className="mt-0.5 text-[11px] text-neutral-500">
                  {row.publishedApps.toLocaleString()} published ·{' '}
                  {storeMetricsUnavailable
                    ? '—'
                    : row.appsWithStoreListing.toLocaleString()}{' '}
                  listed
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium tabular-nums text-neutral-100">
                  {row.publishedApps.toLocaleString()}
                </div>
                <div className="mt-0.5 text-[11px] tabular-nums text-neutral-500">
                  {newShare}% new
                </div>
              </div>
            </div>

            <div className="mt-3 flex h-1 overflow-hidden rounded-full bg-neutral-800/60">
              <span
                className="bg-teal-400/70"
                style={{
                  width: `${widthPct * (row.newApps / Math.max(row.publishedApps, 1))}%`,
                }}
              />
              <span
                className="bg-neutral-500/40"
                style={{
                  width: `${widthPct * (row.existingApps / Math.max(row.publishedApps, 1))}%`,
                }}
              />
            </div>

            <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 text-[11px] text-neutral-500">
              <span>
                Lifetime reviews:{' '}
                <span className="tabular-nums text-neutral-300">
                  {storeMetricsUnavailable
                    ? '—'
                    : formatNullableCount(row.totalReviews, '—')}
                </span>
              </span>
              <span>
                Avg:{' '}
                <span className="tabular-nums text-neutral-300">
                  {storeMetricsUnavailable
                    ? '—'
                    : formatReviewScore(
                        row.averageReviewScore,
                        row.totalReviews,
                        '—',
                      )}
                </span>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function aggregateRatingBuckets(apps: ReportingApp[]): RatingBuckets {
  const buckets = {
    one: null as number | null,
    two: null as number | null,
    three: null as number | null,
    four: null as number | null,
    five: null as number | null,
  };

  for (const app of apps) {
    if (app.oneStarReviews !== null) {
      buckets.one = (buckets.one ?? 0) + app.oneStarReviews;
    }
    if (app.twoStarReviews !== null) {
      buckets.two = (buckets.two ?? 0) + app.twoStarReviews;
    }
    if (app.threeStarReviews !== null) {
      buckets.three = (buckets.three ?? 0) + app.threeStarReviews;
    }
    if (app.fourStarReviews !== null) {
      buckets.four = (buckets.four ?? 0) + app.fourStarReviews;
    }
    if (app.fiveStarReviews !== null) {
      buckets.five = (buckets.five ?? 0) + app.fiveStarReviews;
    }
  }

  return buckets;
}

function reviewWeight(score: number | null, reviews: number | null) {
  if (score === null || reviews === null || reviews === 0) {
    return -Infinity;
  }
  const confidence = Math.min(1, reviews / 50);
  return score * confidence;
}

function sortAndCap(
  apps: ReportingApp[],
  sortFn: (a: ReportingApp, b: ReportingApp) => number,
  limit: number,
  filter?: (app: ReportingApp) => boolean,
) {
  const filtered = filter ? apps.filter(filter) : apps.slice();
  return filtered.sort(sortFn).slice(0, limit);
}
